import React, { useEffect, useRef, useState } from 'react';
import { Camera, Image as ImageIcon, X } from 'lucide-react';

/**
 * Shared camera/gallery image picker (issue #103 / #105).
 * Native file input with capture — not the selfie getUserMedia flow.
 */
export default function ImageCaptureField({
  label = 'Photo (optional)',
  valueUrl = '',
  onChange,
  hint = 'JPG, PNG, WebP · Max 5 MB',
  attachedHint = 'Attached — will be saved with this record',
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (file) return;
    if (valueUrl) {
      setPreview(valueUrl);
      setFileName(String(valueUrl).split('/').pop() || 'Photo');
    } else {
      setPreview('');
      setFileName('');
    }
  }, [valueUrl, file]);

  useEffect(() => () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  const clear = () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onChange?.({ file: null, url: '' });
  };

  const handleChange = (e) => {
    const next = e.target.files?.[0];
    if (!next) return;
    if (!next.type.startsWith('image/')) {
      alert('Please choose an image file (JPG, PNG, etc.)');
      e.target.value = '';
      return;
    }
    if (next.size > 5 * 1024 * 1024) {
      alert('Image must be 5 MB or smaller');
      e.target.value = '';
      return;
    }
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    const blobUrl = URL.createObjectURL(next);
    setFile(next);
    setFileName(next.name);
    setPreview(blobUrl);
    onChange?.({ file: next, url: blobUrl });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-ga-text-muted mb-1">{label}</label>
      {preview ? (
        <div className="flex items-center gap-3 rounded-lg border border-ga-border-subtle bg-ga-bg-page/50 p-3">
          <img
            src={preview}
            alt="Attached preview"
            className="h-16 w-16 rounded-md object-cover border border-ga-border-subtle"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-ga-text-primary">{fileName || 'Photo'}</p>
            <p className="text-xs text-ga-text-muted">{attachedHint}</p>
          </div>
          <button
            type="button"
            onClick={clear}
            className="p-2 rounded-lg text-ga-text-muted hover:bg-ga-danger/10 hover:text-ga-danger"
            aria-label="Remove photo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-ga-border-subtle bg-ga-bg-page/40 px-4 py-6 text-center hover:border-ga-primary/40 hover:bg-ga-primary-soft/30">
          <Camera className="h-7 w-7 text-ga-text-muted" />
          <span className="text-sm font-medium text-ga-text-primary">Take photo or choose image</span>
          <span className="text-xs text-ga-text-muted">{hint}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleChange}
            className="sr-only"
          />
        </label>
      )}
      {!preview && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-ga-primary hover:underline"
        >
          <ImageIcon className="h-3.5 w-3.5" />
          Choose from gallery / files
        </button>
      )}
    </div>
  );
}
